import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  useGetInviteCodesQuery,
  useCreateAndMapActivationCodeMutation,
  useSearchTenantsMutation,
} from "./inviteCodeMutation";

const useInviteCodeQueries = (
  initialPage = 1,
  initialLimit = 10,
  initialStatus = "UNUSED"
) => {
  const location = useLocation();
  const isActive = location.pathname === "/invite-codes";
  const hasInitializedRef = useRef(false);

  // Mark as initialized immediately when page becomes active for the first time
  if (isActive && !hasInitializedRef.current) {
    hasInitializedRef.current = true;
  }

  const [searchState, setSearchState] = useState({
    results: null,
    isSearching: false,
    error: null,
  });

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState(initialStatus);

  const {
    data: inviteCodesData,
    isLoading: isLoadingInviteCodes,
    error: inviteCodesError,
    refetch,
    isFetching,
  } = useGetInviteCodesQuery(
    {
      page,
      limit,
      status,
      searchQuery,
    },
    {
      // Don't refetch on mount/remount - only fetch when arguments change
      refetchOnMountOrArgChange: false,
      skip: !hasInitializedRef.current,
    }
  );

  const [createAndMapInviteCode] = useCreateAndMapActivationCodeMutation();
  const [searchTenants] = useSearchTenantsMutation();

  const handleSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      setSearchState({
        results: null,
        isSearching: false,
        error: null,
      });
      return;
    }

    setSearchState((prev) => ({ ...prev, isSearching: true, error: null }));

    try {
      const result = await searchTenants({
        page: 1,
        limit: 20,
        productId: 100,
        searchText: searchTerm,
      }).unwrap();

      setSearchState({
        results: result,
        isSearching: false,
        error: null,
      });

      return result;
    } catch (error) {
      console.error("Error searching tenants:", error);
      setSearchState({
        results: null,
        isSearching: false,
        error: error.message || "Failed to search tenants",
      });
      throw error;
    }
  };

  const handleMapInviteCode = async (
    tenantId,
    tenantName,
    shouldRefetch = true
  ) => {
    try {
      const body = {
        tenant_id: tenantId,
        tenant_name: tenantName,
      };
      const response = await createAndMapInviteCode({
        body,
      }).unwrap();

      if (shouldRefetch) {
        await refetch();
      } else {
        refetch().catch((error) => {
          console.error("Background refetch failed:", error);
        });
      }

      return response;
    } catch (error) {
      console.error("Error mapping invite code:", error);
      throw error;
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(Math.min(Math.max(newLimit, 1), 2000));
    setPage(1);
  };

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query || "");
    setPage(1);
  };

  const handleStatusChange = (newStatus) => {
    if (["ALL", "USED", "UNUSED"].includes(newStatus)) {
      setStatus(newStatus);
      setPage(1);
    }
  };

  const inviteCodes = inviteCodesData?.inviteCodes || [];
  const totalCount = inviteCodesData?.totalCount || 0;

  return {
    inviteCodes,
    totalCount,
    currentPage: page,
    itemsPerPage: limit,
    searchQuery,
    status,
    searchResults: searchState.results,
    isSearching: searchState.isSearching,
    isLoading: isLoadingInviteCodes || isFetching,
    error:
      searchState.error ||
      (inviteCodesError
        ? {
            message: inviteCodesError.message || "Failed to fetch invite codes",
          }
        : null),
    handleSearch,
    handleMapInviteCode,
    handlePageChange,
    handleLimitChange,
    handleSearchQueryChange,
    handleStatusChange,
    refetch,
  };
};

export default useInviteCodeQueries;
